import React, { useState } from "react";
import closedEye from "assets/icons/closedEye.svg";
import openedEye from "assets/icons/openedEye.svg";
import axiosApi from "core/request";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";

import { Button, Menu, MenuItem } from "@mui/material";

import { langs } from "../../i18n";

import styles from "./ChangePassword.module.scss";

const ChangeUserPassword = () => {
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<{ newPassword: string; confirmPassword: string }>();

  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const isLanguageDropDownOpen = Boolean(anchorEl);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };
  const token = searchParams.get("token");
  const email = searchParams.get("email");

  const encodedURIToken = encodeURIComponent(token as string);

  const onSubmit = async (data: { newPassword: string }) => {
    if (!token || !email) {
      alert("Неверная ссылка для сброса пароля");
      return;
    }

    try {
      await axiosApi.post(
        `account/resetpasswordbytoken?email=${email}&token=${encodedURIToken}`,
        { newPassword: data.newPassword },
      );
      toast.success(t("resetPassword.changePasswordSuccess"));
      navigate("/login");
    } catch (error) {
      toast.error("resetPassword.changePasswordError");
    }
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.card}>
        <div className={styles.langToggle}>
          <Button
            variant="text"
            sx={{
              textTransform: "capitalize",
              fontSize: "1.1rem",
              color: "#0b4d88",
            }}
            id="basic-button"
            aria-controls={isLanguageDropDownOpen ? "basic-menu" : undefined}
            aria-haspopup="true"
            aria-expanded={isLanguageDropDownOpen ? "true" : undefined}
            onClick={handleClick}
          >
            {t("loginForm.dropDownLabel")}
          </Button>
          <Menu
            id="basic-menu"
            sx={{
              boxShadow: "0 4.8px 14.4px rgba(0,0,0,18)",
            }}
            anchorEl={anchorEl}
            open={isLanguageDropDownOpen}
            onClose={handleClose}
          >
            {langs.map((lng) => (
              <MenuItem
                key={lng}
                onClick={() => {
                  i18n.changeLanguage(lng);
                  handleClose();
                }}
              >
                {i18n.t(`languages.${lng}`)}
              </MenuItem>
            ))}
          </Menu>
        </div>
        <h2 className={styles.title}>{t("loginForm.createPassword")}</h2>

        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={styles.formGroup}>
            <label className={styles.label}>{t("loginForm.password")}</label>
            <div className={styles.inputWrapper}>
              <input
                {...register("newPassword", {
                  required: t("loginForm.requiredField") as string,
                  minLength: {
                    value: 8,
                    message: t("validation.minLength8") as string,
                  },
                  validate: (value) => {
                    if (!value) return true;

                    const validations = [
                      {
                        test: /[A-Z]/.test(value),
                        message: t("validation.mustIncludeUpperCase"),
                      },
                      {
                        test: /[!@#$%^&*(),.?":{}|<>]/.test(value),
                        message: t("validation.mustIncludeSpecialChar"),
                      },
                      {
                        test: /\d/.test(value),
                        message: t("validation.mustIncludeDigit"),
                      },
                      {
                        test: value.length >= 8,
                        message: t("validation.minLength8"),
                      },
                    ];

                    const invalid = validations.find((v) => !v.test);
                    return invalid ? invalid.message : true;
                  },
                })}
                type={showPassword ? "text" : "password"}
                className={styles.input}
                placeholder={t("loginForm.password") as string}
              />
              <div
                className={styles.icon}
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? (
                  <img
                    className={styles.show_password}
                    src={openedEye}
                    alt="password eye"
                  />
                ) : (
                  <img
                    className={styles.show_password}
                    src={closedEye}
                    alt="password eye"
                  />
                )}
              </div>
              {errors.newPassword && (
                <p className={styles.error}>{errors.newPassword.message}</p>
              )}
            </div>
          </div>

          <div className={styles.formGroup}>
            <label className={styles.label}>{t("data.confirmPassword")}</label>
            <div className={styles.inputWrapper}>
              <input
                {...register("confirmPassword", {
                  validate: (value) =>
                    value === watch("newPassword") ||
                    (t("validation.passwordsDoNotMatch") as string),
                })}
                type={showConfirm ? "text" : "password"}
                className={styles.input}
                placeholder={t("data.confirmPassword") as string}
              />
              <div
                className={styles.icon}
                onClick={() => setShowConfirm(!showConfirm)}
              >
                {showConfirm ? (
                  <img
                    className={styles.show_confirm}
                    src={openedEye}
                    alt="password eye"
                  />
                ) : (
                  <img
                    className={styles.show_confirm}
                    src={closedEye}
                    alt="password eye"
                  />
                )}
              </div>
              {errors.confirmPassword && (
                <p className={styles.error}>{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>
          <div className={styles.description}>
            <p>{t("password.requirementsTitle")}</p>
            <ul>
              {(t("password.requirements", { returnObjects: true }) as string[]).map(
                (item, index) => (
                  <li key={index}>{item}</li>
                ),
              )}
            </ul>
          </div>

          <button type="submit" className={styles.button}>
            {t("loginForm.loginInSystem")}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ChangeUserPassword;
